import { useState, useEffect, useCallback, useRef } from "react";
import { ChevronDown, Ship } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import {
  setCurrentVessel,
  setVessels,
  setLoading,
  setError,
} from "../../store/vesselOverviewSlice";
import shipIcon from "../../assets/icons/icon-vessel-blue.png";
import { getVesselsList } from "../../services/vesselOverviewService";

const VesselHeader = ({ pageTitle = "Machinery Analytics" }) => {
  const [showVesselDropdown, setShowVesselDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const dispatch = useDispatch();
  const { currentVessel, vessels, loading } = useSelector(
    (state) => state.vesselOverview
  );

  const fetchVessels = useCallback(async () => {
    dispatch(setLoading(true));

    try {
      const vesselData = await getVesselsList();
      dispatch(setVessels(vesselData));

      // Set current vessel once
      const newCurrent =
        vesselData.find((v) => v._id === currentVessel?._id) ||
        vesselData[0] ||
        null;

      dispatch(setCurrentVessel(newCurrent));
    } catch (err) {
      dispatch(setError(err.message));
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, currentVessel?._id]);

  useEffect(() => {
    if (vessels.length === 0) {
      fetchVessels();
    }
  }, [fetchVessels, vessels.length]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowVesselDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleVesselChange = (vessel) => {
    dispatch(setCurrentVessel(vessel));
    setShowVesselDropdown(false);
  };

  if (loading && vessels.length === 0) {
    return <div>Loading vessels...</div>;
  }

  return (
    <div className="relative">
      <div className="flex items-center gap-3" ref={dropdownRef}>
        <img src={shipIcon} alt="Ship Icon" className="w-8 h-6" />

        <div className="flex items-baseline gap-2">
          <div className="relative">
            <button
              onClick={() => setShowVesselDropdown((prev) => !prev)}
              className="flex items-center gap-1 hover:text-[#18325d]"
              aria-expanded={showVesselDropdown}
              aria-haspopup="true"
            >
              <span className="text-2xl font-semibold text-[#27ade3]">
                {currentVessel?.name || "Select Vessel"}
              </span>

              <ChevronDown
                className={`w-4 h-4 mt-1 text-[#18325d] transition-transform duration-200 ${
                  showVesselDropdown ? "rotate-180" : ""
                }`}
              />
            </button>

            {showVesselDropdown && (
              <div className="absolute z-20 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-100 overflow-hidden">
                <div className="py-1 max-h-60 overflow-y-auto">
                  {vessels.length > 0 ? (
                    vessels.map((vessel) => (
                      <button
                        key={vessel._id}
                        className={`w-full text-left px-4 py-2.5 flex items-center gap-2 transition-colors ${
                          vessel._id === currentVessel?._id
                            ? "bg-gradient-to-r from-[#27ade3]/10 to-[#2e6bdb]/10 text-[#2e6bdb]"
                            : "text-gray-700 hover:bg-gray-50"
                        }`}
                        onClick={() => handleVesselChange(vessel)}
                      >
                        <Ship className="w-4 h-4" />
                        <span className="font-medium truncate">
                          {vessel.name}
                        </span>
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-2.5 text-gray-500">
                      No vessels available
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <span className="text-gray-300">/</span>

          <span className="text-lg font-bold text-[#18325d]">
            {pageTitle}
          </span>
        </div>
      </div>
    </div>
  );
};

export default VesselHeader;
